import { Link } from '@tanstack/react-router'
import { CheckCircle2, ShieldCheck, UserCheck } from 'lucide-react'
import type { ReactNode } from 'react'

import type { DualControlResult } from '@/api/types'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'

/**
 * Saying, before the click, that a savings action is countersigned — and saying afterwards what
 * actually happened.
 *
 * Three savings commands work this way: a withdrawal, a closure, and a product definition. Each
 * answers with a `DualControlResult` rather than the thing that was asked for, and
 * `awaitingApproval: true` means the command is stored and *nothing has moved*. On a counter that
 * distinction is not a nicety: a teller who reads "done" counts the notes out of the drawer.
 */

export function SavingsDualControlNotice({
  action,
  approvePermission,
  canCountersign,
  children,
}: {
  /** The action as a noun phrase: "Paying a withdrawal out". */
  action: string
  /** The countersigning code, named so an operator can ask for it by name. */
  approvePermission: string
  canCountersign: boolean
  /** Anything specific to this command that has to be read before the click. */
  children?: ReactNode
}) {
  return (
    <Alert tone={canCountersign ? 'info' : 'warning'}>
      {canCountersign ? <ShieldCheck aria-hidden /> : <UserCheck aria-hidden />}
      <div className="space-y-1">
        <AlertTitle>{action} is countersigned</AlertTitle>
        <AlertDescription>
          {canCountersign ? (
            <>
              Your role holds <span className="font-mono text-xs">{approvePermission}</span>, so this
              may complete as soon as you confirm it. If the branch&apos;s approval policy still calls
              for a second person it is queued instead, and this dialog says so.
            </>
          ) : (
            <>
              Your role does not hold <span className="font-mono text-xs">{approvePermission}</span>,
              so confirming this stores the request and changes nothing yet. Somebody holding that
              permission has to countersign it before it takes effect.
            </>
          )}
        </AlertDescription>
        {children}
      </div>
    </Alert>
  )
}

/**
 * What the server actually did.
 *
 * `awaitingApproval` is the whole of it. There is no third state and nothing is inferred from the
 * absence of an error: a command that answered without `awaitingApproval` executed, and one that
 * answered with it did not.
 */
export function SavingsDualControlOutcome({
  result,
  awaitingDescription,
  appliedDescription,
  children,
}: {
  result: DualControlResult
  /** What is now waiting, in one line. */
  awaitingDescription: ReactNode
  /** What has now changed, in one line. */
  appliedDescription: ReactNode
  /** Detail that belongs under either outcome — a statement to re-read, a fee to expect. */
  children?: ReactNode
}) {
  if (result.awaitingApproval) {
    return (
      <Alert tone="warning">
        <UserCheck aria-hidden />
        <div className="space-y-1">
          <AlertTitle>Sent for approval — nothing has moved</AlertTitle>
          <AlertDescription>
            {awaitingDescription}
            {result.approvalRequestId ? (
              <>
                {' '}
                Request <span className="font-mono text-xs">{result.approvalRequestId}</span>.
              </>
            ) : null}
          </AlertDescription>
          {children}
          <Button asChild variant="secondary" size="sm">
            <Link to="/approvals" search={{ tab: 'mine' }}>
              Track it under Submitted by me
            </Link>
          </Button>
        </div>
      </Alert>
    )
  }

  return (
    <Alert tone="success">
      <CheckCircle2 aria-hidden />
      <div className="space-y-1">
        <AlertTitle>Done</AlertTitle>
        <AlertDescription>{appliedDescription}</AlertDescription>
        {children}
      </div>
    </Alert>
  )
}
